import React, { useEffect, useState } from "react"
import { IReplenishmentDto } from "../../../../types/replenishment/IReplenishmentDto"
import { IErrorModel } from "../../../../types/error/IErrorModel"
import { CommonInput } from "../../../common/element/fields/CommonInput"
import { CommonFileInput } from "../../../common/element/fields/CommonFileInput"
import { IB64File } from "../../../../types/IB64File"
import { CurrencyPicker } from "../../../common/element/fields/CurrencyPicker"
import * as TrinityAPI from "../../../../requests/TrinityAPI"
import { IBrockmanBankDto } from "../../../../types/brockman/IBrockmanBankDto"
import { download, getFieldError, getFormattedDate, round2Digits } from "../../../common/CommonHelper"
import { ModalPopUp } from "../../../common/element/ModalPopUp"
import { IModalMessage } from "../../../../types/error/IModalMessage"
import { CommonFileList } from "../../../common/element/fields/CommonFileList"
import { IMG } from "../../../common/CONST"
import { IClientContractorContract } from "../../../../types/contractor/IClientContractorContract"
import { IClientContractorAgreement } from "../../../../types/contractor/IClientContractorAgreement"
import { ModalConfirm } from "@/components/common/element/ModalConfirm"

export const DocsTab: React.FC<{
  replenishment: IReplenishmentDto
  setReplenishment: (r: IReplenishmentDto) => void
  errorModel: IErrorModel | undefined
  sending: boolean
  submit: () => void
}> = ({ replenishment, setReplenishment, errorModel, sending, submit }) => {
  const [banks, setBanks] = useState<IBrockmanBankDto[]>([])
  const [modalConfirm, setModalConfirm] = useState<IModalMessage>()
  const [message, setMessage] = useState<IModalMessage | null>(null)
  const [templates, setTemplates] = useState<IClientContractorContract[]>([])
  const [template, setTemplate] = useState<IClientContractorContract>()
  const [agreement, setAgreement] = useState<IClientContractorAgreement>()
  const [invoiceAmount, setInvoiceAmount] = useState<string | undefined>(
    replenishment.invoiceAmount ? round2Digits(Number(replenishment.invoiceAmount) / 100).toString() : undefined
  )

  useEffect(() => {
    TrinityAPI.getBrockmanBanks(response => {
      if (response.status === 200) {
        setBanks(response.data)
      }
    })
    if (replenishment.contractorId) {
      TrinityAPI.getContractorTemplates(replenishment.contractorId, response => {
        if (response.status === 200) {
          setTemplates(response.data)
        }
      })
    }
  }, [])

  useEffect(() => {
    if (replenishment.contractId) {
      setTemplate(templates.filter(t => t.id === replenishment.contractId)[0])
    }
  }, [templates])

  useEffect(() => {
    if (template) {
      setReplenishment({
        ...replenishment,
        contractId: template.id,
        contractDate: template.date,
        contractNumber: template.number,
        additionalAgreementId: undefined
      })
    } else {
      setReplenishment({ ...replenishment, contractId: undefined, additionalAgreementId: undefined })
    }
  }, [template])

  useEffect(() => {
    if (agreement) {
      setReplenishment({
        ...replenishment,
        additionalAgreementId: agreement.id,
        additionalAgreementDate: agreement.date,
        additionalAgreementNumber: agreement.number,
        additionalAgreementBankId: agreement.bankId
      })
    } else {
      setReplenishment({ ...replenishment, additionalAgreementId: undefined })
    }
  }, [agreement])

  useEffect(() => {
    setReplenishment({
      ...replenishment,
      invoiceAmount: invoiceAmount
        ? round2Digits(Number(invoiceAmount.replaceAll(" ", "")) * 100).toString()
        : undefined
    })
  }, [invoiceAmount])

  const handleInvoiceChange = (file: IB64File) => {
    setReplenishment({ ...replenishment, invoice: file })
  }

  const handleContractChange = (file: IB64File) => {
    setReplenishment({ ...replenishment, contract: file })
  }

  const handleAgreementUpload = (file: IB64File) => {
    setReplenishment({ ...replenishment, additionalAgreement: file })
  }

  const handleSubmitButton = () => {
    if (replenishment.personalRateRequested) {
      setModalConfirm({
        title: "Вы запросили индивидуальную ставку",
        text: "Вы уверены что хотите получить индивидуальную ставку, а не ставку прописанную в Вашем профиле. Индивидуальная ставка формируется вручную и рассматривается для каждого запроса в течение неопределенного времени.",
        callbackValue: true,
        _callback: doIndividualSubmit
      })

      return
    }

    doSubmit()
  }

  const doIndividualSubmit = (val: any) => {
    if (val) {
      doSubmit()
    }
  }

  const doSubmit = () => {
    if (!replenishment.invoicePath && !replenishment.invoice) {
      setMessage({ title: "Ошибка!", text: "Загрузите файл инвойса" })
      return
    }
    if (!replenishment.contract && !replenishment.contractPath && !replenishment.contractId) {
      setMessage({ title: "Ошибка!", text: "Загрузите файл контракта или выберите шаблон" })
      return
    }
    if (
      !replenishment.additionalAgreement &&
      !replenishment.additionalAgreementPath &&
      !replenishment.additionalAgreementId
    ) {
      setMessage({ title: "Ошибка!", text: "Загрузите файл доп. соглашения или выберите шаблон" })
      return
    }
    if (replenishment.invoiceDate && new Date(replenishment.invoiceDate) > new Date()) {
      setMessage({ title: "Ошибка!", text: "Дата инвойса не может быть в будущем" })
      return
    }
    if (
      replenishment.contractDate &&
      replenishment.invoiceDate &&
      new Date(replenishment.contractDate) > new Date(replenishment.invoiceDate)
    ) {
      setMessage({ title: "Ошибка!", text: "Дата инвойса не может быть раньше даты контракта" })
      return
    }
    if (replenishment.contractDate && new Date(replenishment.contractDate) > new Date()) {
      setMessage({ title: "Ошибка!", text: "Дата контракта не может быть в будущем" })
      return
    }
    if (
      replenishment.contractDate &&
      replenishment.additionalAgreementDate &&
      new Date(replenishment.contractDate) > new Date(replenishment.additionalAgreementDate)
    ) {
      setMessage({
        title: "Ошибка!",
        text: "Дата доп. соглашения не может быть раньше даты контракта"
      })
      return
    }
    submit()
  }

  const isEditable = () => {
    return replenishment.status === "DOC_NAC" || replenishment.status === "NEW"
  }

  if (replenishment.status === "DOC") {
    return (
      <div className="container">
        <div className="container-pop-up">
          <ul className="form-left">
            <li className="form-block-popup">
              <h4 className="heading-chapter">Документы отправлены!</h4>
              <p className="message-course-yes">
                Ваши документы успешно отправлены и находятся на проверке у менеджера. Это может занять некоторое время.
                В случае успешной проверки вы сможете продолжить оформление. В противном случае будет необходимо внести
                исправления в соответствии с комментариями менеджера
              </p>
            </li>
          </ul>
        </div>
      </div>
    )
  } else {
    return (
      <>
        <ModalConfirm message={modalConfirm} setMessage={setModalConfirm} />
        <ModalPopUp message={message} close={() => setMessage(null)} />
        <div
          className="container"
          style={!isEditable() ? { pointerEvents: "none", opacity: 0.7 } : {}}
          aria-disabled={replenishment.status === "DOC_REVIEW"}
        >
          <div className="container-pop-up">
            <ul className="form-left">
              <li className="form-block-popup">
                <h4 className={`heading-chapter${replenishment.invoiceComment ? "-error" : ""}`}>
                  Сумма пополнения в валюте
                </h4>
                {replenishment.invoiceComment ? (
                  <p className="message-course-no">{replenishment.invoiceComment}</p>
                ) : (
                  <></>
                )}
                <div className="flex-row-10 flex-wrap-mobile">
                  <CommonInput
                    typeName={"date"}
                    label={"Дата инвойса"}
                    placeholder={""}
                    name={"invoiceDate"}
                    value={replenishment.invoiceDate}
                    errorModel={errorModel}
                    _callback={e => setReplenishment({ ...replenishment, invoiceDate: e })}
                  />
                  <CommonInput
                    typeName={"text"}
                    label={"Номер инвойса"}
                    placeholder={"Введите номер инвойса"}
                    name={"invoiceNumber"}
                    value={replenishment.invoiceNumber}
                    errorModel={errorModel}
                    _callback={e => setReplenishment({ ...replenishment, invoiceNumber: e })}
                  />
                  <CommonFileInput
                    label={"Инвойс (файл)"}
                    placeholder={replenishment.invoice?.b64 || replenishment.invoicePath ? "Заменить" : "Загрузить"}
                    name={"invoice"}
                    errorModel={errorModel}
                    _callback={handleInvoiceChange}
                    clazzName="fieldset-fit"
                  />
                </div>
                <div className="flex-row-10 flex-wrap-mobile">
                  <CommonInput
                    typeName={"number"}
                    label={"Сумма к оплате"}
                    placeholder={"Введите сумму"}
                    name={"invoiceAmount"}
                    value={invoiceAmount}
                    errorModel={errorModel}
                    _callback={e => setInvoiceAmount(e)}
                  />
                  <CurrencyPicker
                    label={"Валюта"}
                    name={"invoiceCurrency"}
                    errorModel={errorModel}
                    initValue={replenishment.invoiceCurrency}
                    _callback={e => setReplenishment({ ...replenishment, invoiceCurrency: e })}
                    clazzName="fieldset-200"
                  />
                </div>
                <CommonFileList file={replenishment.invoice} />
              </li>

              {templates.length > 0 ? (
                <li className="form-block-popup">
                  <h4 className="heading-chapter">На основании шаблона</h4>
                  <select
                    value={replenishment.contractId}
                    className="input-form"
                    onChange={e => setTemplate(templates.filter(t => t.id === e.target.value)[0])}
                  >
                    <option value="">Выберите шаблон</option>
                    {templates.map((t, index) => (
                      <option value={t.id} key={index}>
                        Контракт {t.name}
                      </option>
                    ))}
                  </select>
                </li>
              ) : (
                <></>
              )}

              {replenishment.contractId ? (
                <></>
              ) : (
                <li className="form-block-popup">
                  <h4 className="heading-chapter">Контракт с заводом</h4>
                  {replenishment.contractComment ? (
                    <p className="message-course-no">{replenishment.contractComment}</p>
                  ) : (
                    <></>
                  )}
                  <div className="flex-row-10 flex-wrap-mobile">
                    <CommonInput
                      typeName={"date"}
                      label={"Дата"}
                      placeholder={""}
                      name={"contractDate"}
                      value={replenishment.contractDate}
                      errorModel={errorModel}
                      _callback={e =>
                        setReplenishment({
                          ...replenishment,
                          contractDate: e
                        })
                      }
                    />
                    <CommonInput
                      typeName={"text"}
                      label={"Номер"}
                      placeholder={"Введите номер"}
                      name={"contractNumber"}
                      value={replenishment.contractNumber}
                      errorModel={errorModel}
                      _callback={e =>
                        setReplenishment({
                          ...replenishment,
                          contractNumber: e
                        })
                      }
                    />
                    <CommonFileInput
                      label={"Контракт (файл)"}
                      placeholder={replenishment.contract?.b64 || replenishment.contractPath ? "Заменить" : "Загрузить"}
                      name={"contract"}
                      errorModel={errorModel}
                      _callback={handleContractChange}
                      clazzName="fieldset-fit"
                    />
                  </div>
                  <CommonFileList file={replenishment.contract} />
                </li>
              )}
            </ul>
            <div className="right-form">
              <div className="form-block-popup">
                <h4 className="heading-chapter">Дополнительное соглашение</h4>
                {replenishment.additionalAgreementComment ? (
                  <p className="message-course-no">{replenishment.additionalAgreementComment}</p>
                ) : (
                  <></>
                )}
                {replenishment.contractId && template ? (
                  <>
                    <fieldset>
                      <legend>На основании шаблона</legend>
                      <select
                        value={replenishment.additionalAgreementId}
                        className="input-form"
                        onChange={e => setAgreement(template?.agreements?.filter(a => a.id === e.target.value)[0])}
                      >
                        <option value="">Выберите шаблон</option>
                        {templates
                          .filter(t => t.id === replenishment.contractId)[0]
                          .agreements?.map((a, index) => (
                            <option value={a.id} key={index}>{`ДС ${a.number} ${getFormattedDate(a.date || "")} ${
                              a.brockmanDetail?.shortName
                            } ${a.brockmanDetail?.bankShortName && `- ${a.brockmanDetail.bankShortName}`}`}</option>
                          ))}
                      </select>
                    </fieldset>
                  </>
                ) : (
                  <></>
                )}
                {replenishment.additionalAgreementId ? (
                  <></>
                ) : (
                  <>
                    <fieldset>
                      <legend>Выберите банк в котором открыт УНК вашего валютного контракта</legend>
                      <select
                        value={replenishment.additionalAgreementBankId}
                        onChange={e =>
                          setReplenishment({
                            ...replenishment,
                            additionalAgreementBankId: e.target.value
                          })
                        }
                        className="input-form"
                      >
                        <option value="">Выберите банк</option>
                        {banks.map((bank, index) => (
                          <option value={bank.id} key={index}>
                            {bank.brockmanShortName || bank.brockman} - {bank.shortName || bank.name}
                          </option>
                        ))}
                      </select>
                      {getFieldError(errorModel, "additionalAgreementBankId") ? (
                        <p className="error-form">{getFieldError(errorModel, "additionalAgreementBankId")}</p>
                      ) : (
                        <></>
                      )}
                      {replenishment.additionalAgreementBankId ? (
                        <button
                          className="btn dowload-bank-btn"
                          onClick={() => download(replenishment.additionalAgreementBankId, "BLANK_AGREEMENT")}
                        >
                          <img src={IMG.download_bank} alt="" className="load-file-new" />
                          Скачать бланк доп. соглашения
                        </button>
                      ) : (
                        <></>
                      )}
                    </fieldset>
                    {replenishment.additionalAgreementBankId ? (
                      <p className="message-course-yes">Скачайте и заполните бланк доп. соглашения и загрузите ниже</p>
                    ) : (
                      <></>
                    )}
                    <div className="flex-row-10">
                      <CommonInput
                        typeName={"date"}
                        label={"Дата"}
                        placeholder={""}
                        name={"additionalAgreementDate"}
                        value={replenishment.additionalAgreementDate}
                        errorModel={errorModel}
                        _callback={e =>
                          setReplenishment({
                            ...replenishment,
                            additionalAgreementDate: e
                          })
                        }
                      />
                      <CommonInput
                        typeName={"text"}
                        label={"Номер"}
                        placeholder={"Введите номер"}
                        name={"additionalAgreementNumber"}
                        value={replenishment.additionalAgreementNumber}
                        errorModel={errorModel}
                        _callback={e =>
                          setReplenishment({
                            ...replenishment,
                            additionalAgreementNumber: e
                          })
                        }
                      />
                    </div>
                    <div className="flex-row-10">
                      <CommonFileInput
                        label={"Доп. соглашение (файл)"}
                        placeholder={
                          replenishment.additionalAgreement?.b64 || replenishment.additionalAgreementPath
                            ? "Заменить"
                            : "Загрузить"
                        }
                        name={"additionalAgreement"}
                        errorModel={errorModel}
                        _callback={handleAgreementUpload}
                      />
                    </div>
                    <CommonFileList file={replenishment.additionalAgreement} />
                  </>
                )}
              </div>
              <div className="form-block-popup">
                <p className="text-block">Запросить индивидуальную ставку комиссии на пополение</p>
                <ul className="user_list-inn">
                  <li className="user_item-inn">
                    <input
                      type="checkbox"
                      className="checkbox"
                      name="course"
                      checked={replenishment?.personalRateRequested}
                      onChange={() =>
                        setReplenishment({
                          ...replenishment,
                          personalRateRequested: !replenishment?.personalRateRequested
                        })
                      }
                      id="cr"
                    />
                    <label htmlFor="cr" className="user_item-check">
                      Индивидуальная ставка
                    </label>
                  </li>
                </ul>
              </div>
            </div>
            {!isEditable() ? (
              <></>
            ) : (
              <div className="flex-row-10">
                <button className="btn" onClick={handleSubmitButton}>
                  {sending ? "Подождите..." : "Отправить на проверку"}
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    )
  }
}
